import { JOURNEYS, SCORE, SPEED } from '@accra/shared';
import { EventBus } from '../core/events';
import type { Actions } from '../core/input/actions';
import { Rng } from '../core/rng';
import { slowest, updateDriving } from '../systems/driving';
import { gradeAt } from '../world/terrain';
import { OncomingFlow } from '../systems/oncoming/oncomingFlow';
import { inQuietZone, uTurnAssist, updateUTurn } from '../systems/uturn/uTurn';
import { answerDash, checkpointAssist, updateCheckpoint, type DashChoice } from '../systems/checkpoint/checkpoints';
import { fuelAssist, updateFuel } from '../systems/fuel/fuel';
import { beginStop, updateStop } from '../systems/passengers/boarding';
import { stopAssist, type StopPhase } from '../systems/passengers/stopAssist';
import { stopAt } from '../systems/passengers/stopSequence';
import { stopIsBusy } from '../systems/passengers/walking';
import { addBonus, addDistanceScore, farePoints } from '../systems/scoring';
import { TrafficSpawner } from '../systems/spawner/trafficSpawner';
import { hootAt, updateTrafficFlow } from '../systems/trafficFlow';
import { collectPickups } from '../systems/pickups/collect';
import { PickupSpawner } from '../systems/pickups/pickupSpawner';
import { isActive, updatePowerUps } from '../systems/pickups/powerUps';
import { updateStar } from '../systems/pickups/roadBonuses';
import { CLEAR_DAY, effectsOf, themeFor, type Conditions } from '@accra/shared';
import { updateCrossTraffic } from '../systems/junctions/crossTraffic';
import { updateRedLight } from '../systems/junctions/redLight';
import { checkPedestrianYield, updateZebra } from '../systems/junctions/zebra';
import { emptyActions } from '../core/input/actions';
import { resolveHits } from './hits';
import { activeRoute, legIndexAt, tripAt, tripEnds } from '../world/route';
import { bindMateCalls } from './mateCalls';
import { createRunState, type RunState, type UpgradeLevels, type VehicleSize } from './RunState';

export interface RunVehicle {
  capacity: number;
  size: VehicleSize;
  /** Garage upgrade levels for each power-up. */
  upgrades?: UpgradeLevels;
  /** Traffic setting from the menu (1 = normal). */
  trafficDensity?: number;
  /** Weather and time of day (from the shift's seed); clear daylight if left out. */
  conditions?: Conditions;
  /** Fuel in the tank at the start (a full tank if left out). */
  fuelStart?: number;
  /** Touch screens: sharp lane changes and quicker acceleration. */
  touchDriving?: boolean;
}

const ANNOUNCE_STOP_AT = 160;
/** How far ahead a hoot reaches, metres. */
const HORN_RANGE = 70;

/** One run of the game: owns the state and ticks every system in order. */
export class Run {
  readonly state: RunState;
  readonly events = new EventBus();
  private readonly rng: Rng;
  private readonly spawner: TrafficSpawner;
  private readonly crossRng: Rng;
  private readonly pickups: PickupSpawner;
  private readonly trafficDensity: number;
  private readonly checkpointRng: Rng;
  private readonly oncomingFlow: OncomingFlow;
  private readonly unbind: () => void;

  constructor(seed: number, vehicle: RunVehicle) {
    this.rng = new Rng(seed);
    this.state = createRunState(vehicle.capacity, SPEED.start, vehicle.size);
    this.state.theme = themeFor(Date.now());
    this.state.conditions = vehicle.conditions ?? CLEAR_DAY;
    this.state.effects = effectsOf(this.state.conditions);
    this.state.bus.grip = this.state.effects.grip;
    this.state.bus.accelFactor = this.state.effects.accel;
    if (vehicle.upgrades) this.state.upgrades = { ...vehicle.upgrades };
    if (vehicle.fuelStart !== undefined) this.state.fuel.units = this.state.fuel.start = vehicle.fuelStart;
    this.trafficDensity = vehicle.trafficDensity ?? 1;
    this.state.bus.touchDriving = vehicle.touchDriving ?? false;
    this.pickups = new PickupSpawner(new Rng(seed ^ 0x3c6ef372));
    this.spawner = new TrafficSpawner(new Rng(seed ^ 0x9e3779b9));
    this.crossRng = new Rng(seed ^ 0x2545f491);
    this.checkpointRng = new Rng(seed ^ 0x5bd1e995);
    this.oncomingFlow = new OncomingFlow(new Rng(seed ^ 0x7f4a7c15));
    beginStop(this.state, 0, this.rng);
    this.events.on('journey:arrived', () => {
      const journey = JOURNEYS[activeRoute().id];
      if (journey) this.state.stats.score += journey.bonusScore;
    });
    this.unbind = bindMateCalls(this.events, new Rng(seed ^ 0x51ed27));
    this.events.on('passenger:dropped', (e) => addBonus(this.state, farePoints(e.farePesewas + e.tipPesewas)));
    this.events.on('stop:rated', (e) => {
      if (e.rating === 'perfect') addBonus(this.state, SCORE.perPerfectStop);
    });
  }

  /** Your answer when a police officer asks for a dash at a checkpoint. */
  answerDash(choice: DashChoice): void {
    answerDash(this.state, choice, this.events);
  }

  update(dt: number, actions: Actions): void {
    const { state } = this;
    if (state.over) return;
    state.stats.seconds += dt;

    const { bus } = state;
    bus.invulnerable = Math.max(0, bus.invulnerable - dt);
    // After a crash the trotro sits still for a moment before you can drive on.
    const recovering = bus.recover > 0;
    bus.recover = Math.max(0, bus.recover - dt);
    const point = stopAt(state.stop.seq);
    const assist = recovering
      ? { maxSpeed: 0 }
      : slowest(stopAssist(bus, point, actions.gas, this.stopPhase()), fuelAssist(state, actions.gas), checkpointAssist(state, actions.gas), uTurnAssist(state));
    bus.grade = gradeAt(bus.s);
    const metres = updateDriving(bus, recovering ? emptyActions() : actions, assist, dt);
    addDistanceScore(state, metres);
    updateFuel(state, metres, actions.gas && !recovering, dt, this.events);
    updateUTurn(state, dt, this.events);
    updateCheckpoint(state, dt, this.checkpointRng, this.events);
    if (state.over) return;

    const legIndex = legIndexAt(state.bus.s);
    // The last stretch before the U-turn is left empty.
    if (inQuietZone(state.bus.s)) this.spawner.holdUntil(state.bus.s);
    else this.spawner.update(state, (activeRoute().legs[legIndex]?.traffic ?? 1) * state.theme.trafficMultiplier * this.trafficDensity);
    if (actions.horn && !recovering) this.honk();
    updatePowerUps(state, dt, this.events);
    updateStar(state, dt);
    if (isActive(state, 'siren')) this.clearAhead(20);
    this.pickups.update(state);
    collectPickups(state, dt, this.events);
    updateTrafficFlow(state, dt);
    this.oncomingFlow.update(state, dt);
    updateCrossTraffic(state, dt, this.crossRng);
    updateZebra(state, dt, this.crossRng);
    updateRedLight(state, this.events);
    checkPedestrianYield(state, this.events);
    resolveHits(state, this.events);
    if (state.over) return;

    updateStop(state, dt, this.rng, this.events);
    this.announceStop();
    this.trackTrip();
    this.trackLeg(legIndex);
  }

  /** Cars ahead in your lane pull over (or speed away if boxed in). Returns how many moved over. */
  private clearAhead(range: number): number {
    const { bus, traffic } = this.state;
    let cleared = 0;
    for (const car of traffic) {
      const ahead = car.s - bus.s;
      if (car.lane !== bus.targetLane || ahead <= 0 || ahead >= range || car.kind.hoppable) continue;
      if (hootAt(car, this.state)) cleared += 1;
    }
    return cleared;
  }

  /** Hoot! */
  private honk(): void {
    this.state.stats.horns += 1;
    this.events.emit('run:horn', { cleared: this.clearAhead(HORN_RANGE) });
  }

  /** The driver parks up and ends the shift. */
  endShift(): void {
    if (this.state.over) return;
    this.state.over = true;
    this.state.endReason = 'endShift';
  }

  dispose(): void {
    this.unbind();
    this.events.clear();
  }

  private stopPhase(): StopPhase {
    if (!this.state.stop.stopped) return 'approach';
    return stopIsBusy(this.state) ? 'holding' : 'done';
  }

  private announceStop(): void {
    const { state } = this;
    const point = stopAt(state.stop.seq);
    const distance = point.position - state.bus.s;
    if (!state.stop.approachAnnounced && distance < ANNOUNCE_STOP_AT) {
      state.stop.approachAnnounced = true;
      this.events.emit('stop:approaching', { stopName: point.stop.name, distance });
    }
  }

  /** Trips: out to the terminus, U-turn, and back on the other carriageway. */
  private trackTrip(): void {
    const { trip } = tripAt(this.state.bus.s);
    if (trip === this.state.trip) return;
    this.state.trip = trip;
    const ends = tripEnds(trip);
    this.events.emit('trip:started', { trip: trip + 1, from: ends.from, to: ends.to });
    // Keep the U-turn banner on screen: the first leg back is the one we're already in.
    this.state.legIndex = legIndexAt(this.state.bus.s);
  }

  private trackLeg(legIndex: number): void {
    if (legIndex === this.state.legIndex) return;
    this.state.legIndex = legIndex;
    const leg = activeRoute().legs[legIndex];
    if (leg) this.events.emit('leg:entered', { legId: leg.id, name: leg.name, subtitle: leg.subtitle });
  }
}
